import { useState, useRef } from 'react';
import { Upload, X, Link, Image, Loader2, Check, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import api from '@/lib/api';
import { getImageUrl } from '@/lib/utils';

export default function ImageUpload({ 
  value, 
  onChange, 
  label = "Image",
  accept = "image/*",
  maxSize = 10, // MB
  showPreview = true,
  className = ""
}) {
  const [open, setOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [urlInput, setUrlInput] = useState('');
  const [activeTab, setActiveTab] = useState('upload');
  const fileInputRef = useRef(null);

  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Check file size
    if (file.size > maxSize * 1024 * 1024) {
      toast.error(`File too large. Maximum size is ${maxSize}MB`);
      return;
    }

    setUploading(true);
    try {
      // Determine resource type and folder
      const isVideo = file.type.startsWith('video/');
      const resourceType = isVideo ? 'video' : 'image';
      const folder = 'site';

      // First, try Cloudinary upload
      let cloudinaryConfigured = false;
      try {
        const statusRes = await api.get('/cloudinary/status');
        cloudinaryConfigured = statusRes.data.configured;
      } catch {
        cloudinaryConfigured = false;
      }

      if (cloudinaryConfigured) {
        // Use Cloudinary for permanent storage
        const formData = new FormData();
        formData.append('file', file);

        const response = await api.post(`/cloudinary/upload?folder=${folder}`, formData, {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        });

        if (response.data.success) {
          // Cloudinary returns the full CDN URL
          onChange(response.data.url);
          toast.success('Image uploaded to cloud storage');
          setOpen(false);
        }
      } else {
        // Fallback to local storage (for development/preview)
        const formData = new FormData();
        formData.append('file', file);

        const response = await api.post('/admin/upload-file', formData, {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        });

        if (response.data.success) {
          // Store the relative URL - getImageUrl() will resolve it at render time
          onChange(response.data.url);
          toast.success('Image uploaded successfully');
          setOpen(false);
        }
      }
    } catch (error) {
      console.error('Upload error:', error);
      toast.error(error.response?.data?.detail || 'Failed to upload image');
    } finally {
      setUploading(false);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleUrlSubmit = () => {
    if (!urlInput.trim()) {
      toast.error('Please enter a URL');
      return;
    }

    // Basic URL validation
    if (!urlInput.startsWith('http://') && !urlInput.startsWith('https://')) {
      toast.error('Please enter a valid URL starting with http:// or https://');
      return;
    }

    onChange(urlInput.trim());
    toast.success('Image URL saved');
    setUrlInput('');
    setOpen(false);
  };

  const handleRemove = () => {
    onChange('');
  };

  const handleDrop = async (e) => {
    e.preventDefault();
    const file = e.dataTransfer.files?.[0];
    if (file) {
      // Simulate file input change
      const dataTransfer = new DataTransfer();
      dataTransfer.items.add(file);
      if (fileInputRef.current) {
        fileInputRef.current.files = dataTransfer.files;
        handleFileSelect({ target: { files: dataTransfer.files } });
      }
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  return (
    <div className={className}>
      {label && <Label className="mb-2 block">{label}</Label>}
      
      {/* Current Image Preview */}
      {value && showPreview ? (
        <div className="relative group">
          <div className="relative w-full h-40 rounded-lg border border-stone-200 overflow-hidden bg-stone-50">
            <img 
              src={getImageUrl(value)} 
              alt="Preview" 
              className="w-full h-full object-cover"
              onError={(e) => {
                e.target.style.display = 'none';
                e.target.nextSibling.style.display = 'flex';
              }}
            />
            <div className="hidden absolute inset-0 items-center justify-center text-stone-400">
              <Image className="h-8 w-8" />
            </div>
          </div>
          <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
            <Button 
              size="sm" 
              variant="secondary"
              onClick={() => setOpen(true)}
            >
              Change
            </Button>
            <Button 
              size="sm" 
              variant="destructive"
              onClick={handleRemove}
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        </div>
      ) : (
        <Button 
          type="button"
          variant="outline" 
          className="w-full h-32 border-dashed"
          onClick={() => setOpen(true)}
        >
          <div className="flex flex-col items-center gap-2 text-stone-500">
            <Upload className="h-8 w-8" />
            <span>Click to upload image</span>
            <span className="text-xs">or paste URL</span>
          </div>
        </Button>
      )}

      {/* Upload Dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Add Image</DialogTitle>
            <DialogDescription>
              Upload an image from your computer or enter a URL
            </DialogDescription>
          </DialogHeader>

          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="upload">
                <Upload className="h-4 w-4 mr-2" />
                Upload
              </TabsTrigger>
              <TabsTrigger value="url">
                <Link className="h-4 w-4 mr-2" />
                URL
              </TabsTrigger>
            </TabsList>

            <TabsContent value="upload" className="mt-4">
              <div
                className="border-2 border-dashed border-stone-300 rounded-lg p-8 text-center hover:border-stone-400 transition-colors cursor-pointer"
                onClick={() => fileInputRef.current?.click()}
                onDrop={handleDrop}
                onDragOver={handleDragOver}
              >
                {uploading ? (
                  <div className="flex flex-col items-center gap-2">
                    <Loader2 className="h-10 w-10 animate-spin text-stone-400" />
                    <p className="text-sm text-stone-500">Uploading...</p>
                  </div>
                ) : (
                  <>
                    <Upload className="h-10 w-10 mx-auto text-stone-400 mb-3" />
                    <p className="text-sm font-medium mb-1">
                      Click to upload or drag and drop
                    </p>
                    <p className="text-xs text-stone-500">
                      PNG, JPG, GIF, WEBP, SVG up to {maxSize}MB
                    </p>
                  </>
                )}
                <input
                  ref={fileInputRef}
                  type="file"
                  accept={accept}
                  onChange={handleFileSelect}
                  className="hidden"
                />
              </div>
            </TabsContent>

            <TabsContent value="url" className="mt-4">
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label>Image URL</Label>
                  <Input
                    type="url"
                    placeholder="https://example.com/image.jpg"
                    value={urlInput}
                    onChange={(e) => setUrlInput(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && handleUrlSubmit()}
                  />
                </div>
                {urlInput && (
                  <div className="rounded-lg border p-2">
                    <p className="text-xs text-stone-500 mb-2">Preview:</p>
                    <img 
                      src={urlInput} 
                      alt="Preview" 
                      className="max-h-32 mx-auto rounded"
                      onError={(e) => {
                        e.target.style.display = 'none';
                      }}
                    />
                  </div>
                )}
                <Button 
                  onClick={handleUrlSubmit} 
                  className="w-full"
                  disabled={!urlInput.trim()}
                >
                  <Check className="h-4 w-4 mr-2" />
                  Use This URL
                </Button>
              </div>
            </TabsContent>
          </Tabs>
        </DialogContent>
      </Dialog>
    </div>
  );
}
